import { isTeamCoOwnerActor, isTeamOwnerActor } from './teamConfig';
import {
  CONDITION_OPERATOR_OPTIONS,
  applyDerivedPublishingFields,
  buildPublishingScopeKey,
  deriveDisplayState,
  deriveVisibilityAudience,
  findDuplicatePublishingRule,
  formatConditionLine,
  getAvailableConditionFields,
  getConditionValueOptions,
  getDisplayAreaLabel,
  getDisplayAreasForModule,
  getDisplayStateLabel,
  getPublishingActionLabel,
  getPublishingActionsForScope,
  getPublishingModuleLabel,
  getReferenceAvailabilityLabel,
  getRequiredSourceContext,
  getSourceOwner,
  getSourceResultLabel,
  getSourceResultReference,
  getSourceResultsForDisplayArea,
  getVisibilityAudienceLabel,
  isGuaranteedContextConditionField,
  isScopeRepeatConditionField,
  isSourceContextDefined,
  isSourceResultAvailable,
  namesMatch,
  nextPublishingRuleId,
  normalizePublishingForm,
  operatorIsMulti,
} from './publishingConfig';
import { emitRuleSaveHistory } from './changeHistoryEmitters';

const ACTOR = {
  name: 'Olivia Brown',
  role: 'Configuration Admin',
  team: 'Platform Operations',
  accountId: 'ADM-0002',
};

export function publishingActor() {
  return { ...ACTOR };
}

export function canAccessPublishingConfiguration(auth) {
  return isTeamOwnerActor(auth) || isTeamCoOwnerActor(auth);
}

export function canViewPublishingConfiguration(auth) {
  return canAccessPublishingConfiguration(auth);
}

export function canAddPublishingRule(auth) {
  return canAccessPublishingConfiguration(auth);
}

export function canEditPublishingRule(auth) {
  return canAccessPublishingConfiguration(auth);
}

export function canSavePublishingRule(auth) {
  return canAccessPublishingConfiguration(auth);
}

function addError(result, key, message) {
  if (!result.summary.includes(message)) result.summary.push(message);
  if (key && !result.errors[key]) result.errors[key] = message;
}

function conditionValues(condition) {
  return [...new Set((condition?.values || []).map((value) => String(value || '').trim()).filter(Boolean))];
}

function conditionFingerprint(condition) {
  return [
    condition.field || '',
    condition.operator || '',
    conditionValues(condition).slice().sort().join(','),
  ].join('|');
}

function setsOverlap(a, b) {
  return a.some((value) => b.includes(value));
}

function conditionsCannotAllBeTrue(conditions = []) {
  const byField = {};
  conditions.forEach((condition) => {
    if (!condition.field || !condition.operator) return;
    const values = conditionValues(condition);
    if (!values.length) return;
    if (!byField[condition.field]) byField[condition.field] = [];
    byField[condition.field].push({ operator: condition.operator, values });
  });

  return Object.values(byField).some((entries) => {
    const equals = entries.filter((entry) => entry.operator === 'equals').flatMap((entry) => entry.values);
    const uniqueEquals = [...new Set(equals)];
    if (uniqueEquals.length > 1) return true;

    const anyOf = entries.filter((entry) => entry.operator === 'is_any_of').map((entry) => entry.values);
    if (anyOf.length > 1) {
      const intersection = anyOf.reduce((acc, set) => acc.filter((value) => set.includes(value)));
      if (!intersection.length) return true;
    }

    const positives = [
      ...uniqueEquals,
      ...(anyOf.length ? anyOf.reduce((acc, set) => acc.filter((value) => set.includes(value))) : []),
    ];
    const uniquePositives = [...new Set(positives)];

    return entries.some((entry) => {
      if (entry.operator === 'does_not_equal' && uniquePositives.includes(entry.values[0])) return true;
      if (entry.operator === 'is_not_any_of' && uniquePositives.length && uniquePositives.every((value) => entry.values.includes(value))) {
        return true;
      }
      if (entry.operator === 'is_any_of' && uniqueEquals.length && !setsOverlap(entry.values, uniqueEquals)) return true;
      return false;
    });
  });
}

export function collectPublishingSaveErrors(formData, allRules, excludeId, options = {}) {
  const form = applyDerivedPublishingFields(normalizePublishingForm(formData));
  const result = { errors: {}, summary: [] };
  const others = (allRules || []).filter((rule) => rule.id !== excludeId).map(normalizePublishingForm);

  if (options.canSave === false) {
    addError(result, 'permission', 'You do not have permission for this action.');
    return result;
  }

  if (options.checksAvailable === false) {
    addError(result, 'checksUnavailable', 'The required checks could not be completed. Your changes have not been saved.');
    return result;
  }

  if (!String(form.ruleName || '').trim()) addError(result, 'ruleName', 'Enter a Rule Name.');
  else if (others.some((rule) => namesMatch(rule.ruleName, form.ruleName))) {
    addError(result, 'ruleName', 'A Publishing Rule already uses this name.');
  }

  if (!String(form.description || '').trim()) addError(result, 'description', 'Enter an Internal Description.');
  if (!form.module) addError(result, 'module', 'Select a Module.');

  const areaOptions = getDisplayAreasForModule(form.module);
  if (!form.displayArea) {
    addError(result, 'displayArea', areaOptions.length ? 'Select a Display Area supported by this Module.' : 'No compatible choices available');
  } else if (form.module && !areaOptions.some((option) => option.value === form.displayArea)) {
    addError(result, 'displayArea', 'Select a Display Area supported by this Module.');
  }

  const sourceOptions = getSourceResultsForDisplayArea(form.displayArea, { includeUnavailable: true });
  if (!form.sourceResult) {
    addError(result, 'sourceResult', sourceOptions.length
      ? 'Select an available Source Result supported by this Module and Display Area.'
      : 'No compatible choices available');
  } else if (form.displayArea && !sourceOptions.some((option) => option.value === form.sourceResult)) {
    addError(result, 'sourceResult', 'Select an available Source Result supported by this Module and Display Area.');
  } else if (form.sourceResult && !isSourceResultAvailable(form.sourceResult)) {
    addError(result, 'sourceResult', 'This reference is unavailable or incompatible.');
  } else if (form.sourceResult && (!isSourceContextDefined(form.sourceResult) || !getSourceOwner(form.sourceResult, form.module))) {
    addError(result, 'sourceResult', 'The required Source Context is incomplete, unavailable or incompatible.');
  }

  const actionOptions = getPublishingActionsForScope(form.displayArea, form.sourceResult);
  if (!form.publishingAction) {
    addError(result, 'publishingAction', actionOptions.length
      ? 'Select a Publishing Action supported by this Source Result and Display Area.'
      : 'No compatible choices available');
  } else if (!actionOptions.some((option) => option.value === form.publishingAction)) {
    addError(result, 'publishingAction', 'Select a Publishing Action supported by this Source Result and Display Area.');
  }

  const displayState = deriveDisplayState(form.publishingAction);
  if (form.publishingAction && !displayState) {
    addError(result, 'displayState', 'The Display State or Visibility Audience could not be determined.');
  }
  const audience = deriveVisibilityAudience(form.displayArea, form.publishingAction);
  if (form.publishingAction && form.displayArea && !audience) {
    addError(result, 'visibilityAudience', 'The Display State or Visibility Audience could not be determined.');
  }

  if (!String(form.changeReason || '').trim()) addError(result, 'changeReason', 'Enter a Change Reason.');

  const dup = findDuplicatePublishingRule(others, form);
  if (dup) addError(result, 'duplicate', `A Publishing Rule already exists for this scope: ${dup.id}.`);

  if (excludeId && options.originalScopeKey) {
    const currentKey = buildPublishingScopeKey(form);
    if (currentKey !== options.originalScopeKey) {
      addError(result, 'scope', 'Module, Display Area and Source Result cannot be changed after Save.');
    }
  }

  const availableFields = getAvailableConditionFields(form).map((option) => option.value);
  const seenFingerprints = [];
  (form.conditions || []).forEach((condition, index) => {
    const key = `condition_${index}`;
    const values = conditionValues(condition);
    if (!condition.field || !condition.operator || !values.length) {
      addError(result, key, 'Complete or remove this Condition.');
      return;
    }
    if (!CONDITION_OPERATOR_OPTIONS.some((option) => option.value === condition.operator)) {
      addError(result, key, 'This Condition is unavailable or incompatible with the selected scope.');
    }
    if (!operatorIsMulti(condition.operator) && values.length !== 1) {
      addError(result, key, 'Complete or remove this Condition.');
    }
    if (isScopeRepeatConditionField(condition.field) || isGuaranteedContextConditionField(condition.field, form.sourceResult)) {
      addError(result, key, 'This Condition repeats information already defined by the Rule Scope or Source Result.');
    } else if (!availableFields.includes(condition.field)) {
      addError(result, key, 'This Condition is unavailable or incompatible with the selected scope.');
    }
    const valueOptions = getConditionValueOptions(condition.field).map((option) => option.value);
    if (values.some((value) => valueOptions.length && !valueOptions.includes(value))) {
      addError(result, key, 'This reference is unavailable or incompatible.');
    }
    const fingerprint = conditionFingerprint(condition);
    if (seenFingerprints.includes(fingerprint)) addError(result, key, 'This Condition is already included in the Rule.');
    seenFingerprints.push(fingerprint);
  });

  if (conditionsCannotAllBeTrue(form.conditions)) {
    addError(result, 'conditions', 'These Conditions cannot all be true together.');
  }

  if (excludeId && options.originalLastUpdated) {
    const current = (allRules || []).find((rule) => rule.id === excludeId);
    if (current && (current.lastUpdated || current.modifiedOn) && (current.lastUpdated || current.modifiedOn) !== options.originalLastUpdated) {
      addError(result, 'concurrent', 'This Rule has changed since you opened it. Open the latest Rule before saving.');
    }
  }

  return result;
}

export function ruleSnapshotFromForm(formData) {
  const form = applyDerivedPublishingFields(normalizePublishingForm(formData));
  return {
    ruleName: String(form.ruleName || '').trim(),
    description: String(form.description || '').trim(),
    module: form.module,
    displayArea: form.displayArea,
    sourceResult: form.sourceResult,
    publishingAction: form.publishingAction,
    displayState: form.displayState,
    visibilityAudience: form.visibilityAudience,
    conditions: (form.conditions || []).map((condition) => ({
      id: condition.id,
      field: condition.field,
      operator: condition.operator,
      values: conditionValues(condition),
    })),
  };
}

function snapshotsEqual(a, b) {
  return JSON.stringify(a) === JSON.stringify(b);
}

export function getPublishedRule(rule) {
  if (!rule) return null;
  const { pendingDraft, ...published } = rule;
  return normalizePublishingForm(published);
}

export function getEditRuleSource(rule) {
  if (!rule) return null;
  return normalizePublishingForm(rule);
}

export function appendTestHistory(rule, summary, passed) {
  const actor = publishingActor();
  const base = Array.isArray(rule.testHistory) ? rule.testHistory : [];
  return [
    ...base,
    {
      actor: actor.name,
      role: actor.role,
      accountId: actor.accountId,
      timestamp: new Date().toISOString(),
      result: passed ? 'passed' : 'blocked',
      summary,
    },
  ];
}

export function mergeRulesAfterSave(allRules, payload, publishedBaseline) {
  const snapshot = ruleSnapshotFromForm(payload);
  const isNew = !publishedBaseline?.id;
  const previousSnapshot = publishedBaseline?.id ? ruleSnapshotFromForm(publishedBaseline) : null;

  if (!isNew && previousSnapshot && snapshotsEqual(previousSnapshot, snapshot)) {
    return {
      rules: allRules,
      record: getPublishedRule(publishedBaseline),
      unchanged: true,
      isNew: false,
    };
  }

  const nowIso = payload.lastUpdated || new Date().toISOString();
  const actor = payload.lastUpdatedBy || ACTOR.name;
  const id = payload.id || publishedBaseline?.id || nextPublishingRuleId(allRules);
  const record = {
    ...snapshot,
    id,
    dateAdded: isNew ? nowIso : publishedBaseline.dateAdded || publishedBaseline.createdOn || nowIso,
    addedBy: isNew ? actor : publishedBaseline.addedBy || publishedBaseline.createdBy || actor,
    lastUpdated: nowIso,
    lastUpdatedBy: actor,
    changeReason: String(payload.changeReason || '').trim(),
    testHistory: payload.testHistory || publishedBaseline?.testHistory || [],
    configurationHistory: payload.configurationHistory || publishedBaseline?.configurationHistory || [],
  };
  try {
    emitRuleSaveHistory({
      isNew,
      previous: publishedBaseline,
      next: record,
      module: 'Configuration — Publishing',
      sourceModuleRef: 'configuration_publishing',
      changedItem: record.ruleName || record.name || id,
      itemId: id,
      changeReason: record.changeReason,
      actor,
      stamp: nowIso,
      compareKeys: [
        'ruleName',
        'description',
        'module',
        'displayArea',
        'sourceResult',
        'publishingAction',
        'displayState',
        'visibilityAudience',
        'conditions',
      ],
    });
  } catch {
    /* history recording failure must not reverse the source save */
  }
  if (isNew) {
    return {
      rules: [...allRules.filter((rule) => rule.id !== record.id), record],
      record,
      unchanged: false,
      isNew: true,
    };
  }
  return {
    rules: allRules.map((rule) => (rule.id === record.id ? { ...rule, ...record } : rule)),
    record,
    unchanged: false,
    isNew: false,
  };
}

export function savePublishingRule(allRules, payload, publishedBaseline, options = {}) {
  const excludeId = publishedBaseline?.id || payload?.id || undefined;
  const validation = collectPublishingSaveErrors(payload, allRules, excludeId, {
    originalLastUpdated:
      options.originalLastUpdated
      ?? publishedBaseline?.lastUpdated
      ?? publishedBaseline?.modifiedOn,
    originalScopeKey:
      options.originalScopeKey
      ?? (publishedBaseline ? buildPublishingScopeKey(publishedBaseline) : undefined),
    canSave: options.canSave !== false,
    checksAvailable: options.checksAvailable !== false,
  });

  if (validation.summary.length) {
    return {
      success: false,
      errors: validation.errors,
      summary: validation.summary,
      rules: allRules,
      unchanged: false,
    };
  }

  try {
    const merged = mergeRulesAfterSave(allRules, payload, publishedBaseline || {});
    return {
      success: true,
      errors: {},
      summary: [],
      rules: merged.rules,
      record: merged.record,
      unchanged: Boolean(merged.unchanged),
      isNew: Boolean(merged.isNew),
    };
  } catch {
    return {
      success: false,
      errors: { form: 'Your changes could not be saved. Your entries have been kept.' },
      summary: ['Your changes could not be saved. Your entries have been kept.'],
      rules: allRules,
      unchanged: false,
    };
  }
}

export function mergeRulesAfterActivate(allRules, payload, publishedBaseline) {
  const result = savePublishingRule(
    allRules,
    { ...payload, changeReason: payload.changeReason || 'Save from configuration test' },
    publishedBaseline || {},
    { canSave: true, checksAvailable: true },
  );
  return result.rules;
}

export function collectActivationErrors(formData, allRules, excludeId) {
  return collectPublishingSaveErrors(formData, allRules, excludeId).summary;
}

export function collectPublishingSaveErrorsForForm(formData, allRules, excludeId, options) {
  return collectPublishingSaveErrors(formData, allRules, excludeId, options);
}

export function runPublishingRuleTest(formData, allRules) {
  const snapshot = ruleSnapshotFromForm(formData);
  const validation = collectPublishingSaveErrors(formData, allRules, formData.id || undefined);
  const lines = [
    `Module: ${getPublishingModuleLabel(snapshot.module) || '—'}`,
    `Display Area: ${getDisplayAreaLabel(snapshot.displayArea) || '—'}`,
    `Source Result: ${getSourceResultLabel(snapshot.sourceResult) || '—'}`,
    `Source Owner: ${getSourceOwner(snapshot.sourceResult, snapshot.module) || '—'}`,
    `Required Source Context: ${getRequiredSourceContext(snapshot.sourceResult) || 'None'}`,
    `Publishing Action: ${getPublishingActionLabel(snapshot.publishingAction) || '—'}`,
    `Display State: ${getDisplayStateLabel(snapshot.displayState) || '—'}`,
    `Visibility Audience: ${getVisibilityAudienceLabel(snapshot.visibilityAudience) || '—'}`,
    `Reference availability: ${getReferenceAvailabilityLabel(snapshot.sourceResult)}`,
  ];
  if ((snapshot.conditions || []).length) {
    lines.push('', 'Conditions:', ...snapshot.conditions.map((condition) => `• ${formatConditionLine(condition)}`));
  } else {
    lines.push('', 'No Conditions');
  }
  if (validation.summary.length) {
    return {
      passed: false,
      summary: 'Rule test blocked. Configuration conflicts or missing requirements were found.',
      detail: [...lines, '', 'Issues:', ...validation.summary.map((message) => `• ${message}`)].join('\n'),
    };
  }
  return {
    passed: true,
    summary: 'Rule test passed. Configuration is valid — no live visibility was changed.',
    detail: [...lines, '', 'No duplicate-scope or incompatible settings detected.'].join('\n'),
  };
}

export function fieldLabel(key) {
  const labels = {
    ruleName: 'Rule Name',
    description: 'Internal Description',
    module: 'Module',
    displayArea: 'Display Area',
    sourceResult: 'Source Result',
    publishingAction: 'Publishing Action',
    displayState: 'Display State',
    visibilityAudience: 'Visibility Audience',
  };
  return labels[key] || key;
}

export function displayFieldValue(key, value, context = {}) {
  if (value == null || value === '') return '—';
  if (key === 'module') return getPublishingModuleLabel(value);
  if (key === 'displayArea' || key === 'surface') return getDisplayAreaLabel(value);
  if (key === 'sourceResult') return getSourceResultLabel(value);
  if (key === 'publishingAction') return getPublishingActionLabel(value);
  if (key === 'displayState') return getDisplayStateLabel(value);
  if (key === 'visibilityAudience') return getVisibilityAudienceLabel(value);
  if (key === 'sourceOwner') return getSourceOwner(context.sourceResult, context.module);
  if (key === 'sourceReference') return getSourceResultReference(value || context.sourceResult);
  return String(value);
}

export function mergeRulesAfterSaveDraft(allRules, payload, publishedBaseline) {
  return mergeRulesAfterSave(allRules, payload, publishedBaseline).rules;
}

export function mergeRulesAfterInactivate(allRules) {
  return allRules;
}

export function mergeRulesAfterListActivate(allRules) {
  return allRules;
}
